import { Calendar, LayoutPanelLeft } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import ThreeDCube from '@ui/components/icons/ThreeDCube';
import { formatDateAsLocalString, parseUtcDateString } from '@website/utils/date';

export const DASHBOARD_PARAMS = {
  DATE_FROM: 'dateFrom',
  DATE_TO: 'dateTo',
  PERIOD: 'period',
  ACTIVITY_ID: 'activityId'
} as const;

interface CustomDateRange {
  from: Date | null;
  to: Date | null;
}

interface ActivityOption {
  value: string;
  label: string;
}

export const useDashboardFilters = (activities: ActivityOption[] = []) => {
  const t = useTranslations('Dashboard');
  const locale = useLocale();
  const router = useRouter();
  const searchParams = useSearchParams();

  const dateRange = useMemo(
    () => ({
      from: searchParams.get(DASHBOARD_PARAMS.DATE_FROM)
        ? parseUtcDateString(searchParams.get(DASHBOARD_PARAMS.DATE_FROM)!)
        : null,
      to: searchParams.get(DASHBOARD_PARAMS.DATE_TO)
        ? parseUtcDateString(searchParams.get(DASHBOARD_PARAMS.DATE_TO)!)
        : null
    }),
    [searchParams]
  );

  const getDateRangeLabel = useCallback(() => {
    if (dateRange.to) {
      return dateRange.to.toLocaleDateString(locale);
    }
    return new Date().toLocaleDateString(locale);
  }, [dateRange, locale]);

  const filters = useMemo(
    () => [
      {
        id: DASHBOARD_PARAMS.DATE_FROM,
        label: getDateRangeLabel(),
        icon: <Calendar className="w-4 h-4" />,
        type: 'dateRange' as const
      },
      {
        id: DASHBOARD_PARAMS.PERIOD,
        label: t('daily'),
        icon: <LayoutPanelLeft className="w-4 h-4" />,
        type: 'select' as const,
        options: [
          { value: 'daily', label: t('daily') },
          { value: 'weekly', label: t('weekly') },
          { value: 'monthly', label: t('monthly') },
          { value: 'yearly', label: t('yearly') }
        ]
      },
      {
        id: DASHBOARD_PARAMS.ACTIVITY_ID,
        label: t('activity'),
        icon: <ThreeDCube className="w-4 h-4" />,
        type: 'select' as const,
        options: [{ value: '', label: t('allActivities') }, ...activities]
      }
    ],
    [getDateRangeLabel, t, activities]
  );

  const handleFilterChange = useCallback(
    (filterId: string, value: string | CustomDateRange) => {
      const params = new URLSearchParams(searchParams.toString());

      if (filterId === DASHBOARD_PARAMS.DATE_FROM && typeof value === 'object' && 'from' in value) {
        // Check if we have valid dates or if we're resetting (null values)
        if (value.from && value.to) {
          params.set(DASHBOARD_PARAMS.DATE_FROM, formatDateAsLocalString(value.from));
          params.set(DASHBOARD_PARAMS.DATE_TO, formatDateAsLocalString(value.to));
        } else {
          // Reset case: remove both date parameters
          params.delete(DASHBOARD_PARAMS.DATE_FROM);
          params.delete(DASHBOARD_PARAMS.DATE_TO);
        }
      } else if (typeof value === 'string') {
        if (value === '' || !value) {
          params.delete(filterId);
        } else {
          params.set(filterId, value);
        }
      }

      router.push(`?${params.toString()}`);
    },
    [searchParams, router]
  );

  const getFilterValue = useCallback(
    (filterId: string): string => {
      return searchParams.get(filterId) || '';
    },
    [searchParams]
  );

  const getDateRange = useCallback((): { from: Date | null; to: Date | null } => {
    const dateFrom = searchParams.get(DASHBOARD_PARAMS.DATE_FROM);
    const dateTo = searchParams.get(DASHBOARD_PARAMS.DATE_TO);

    return {
      from: dateFrom ? parseUtcDateString(dateFrom) : null,
      to: dateTo ? parseUtcDateString(dateTo) : null
    };
  }, [searchParams]);

  const getCurrentFilters = useCallback(() => {
    const dateFrom = searchParams.get(DASHBOARD_PARAMS.DATE_FROM);
    const dateTo = searchParams.get(DASHBOARD_PARAMS.DATE_TO);

    return {
      dateFrom,
      dateTo,
      startDate: dateFrom,
      endDate: dateTo
    };
  }, [searchParams]);

  return {
    filters,
    handleFilterChange,
    getFilterValue,
    getDateRange,
    getCurrentFilters
  };
};
